import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';
import { DiagnosticsService } from './diagnostics-service';

// Owns the single-admin experience: the hidden login modal, the admin menu and the diagnostics
// log toggle. Admin identity is established via the server BFF (/api/auth/me). The web app never
// scrapes; ingestion is the tray scraper's job, and image-cache fix-ups live in ImageCacheAdminService.
@singleton()
export class AdminService {
  private readonly http: HttpClient = resolve(HttpClient);
  private readonly diagnostics: DiagnosticsService = resolve(DiagnosticsService);

  isAdmin: boolean = false;
  loginConfigured: boolean = false;
  showLogin: boolean = false;
  loggingIn: boolean = false;
  loginUsername: string = '';
  loginPassword: string = '';
  loginError: string | null = null;

  logExpanded: boolean = false;
  adminMenuOpen: boolean = false;

  // Resolves the current admin identity from the server BFF. Safe to call unconditionally on
  // startup; anonymous visitors simply resolve to a non-admin state.
  async checkAuth(): Promise<void> {
    try {
      const response = await this.http.get('/api/auth/me');
      if (!response.ok) return;
      const data = await response.json();
      this.isAdmin = data.isAdmin === true;
      this.loginConfigured = data.loginConfigured === true;
    } catch {
      // Non-fatal: treat as anonymous.
      this.isAdmin = false;
    }
  }

  openLogin(event?: MouseEvent): void {
    // The hidden corner hotspot only reveals the login when clicked while holding
    // Shift+Ctrl+Alt together, so an accidental corner click does nothing.
    if (event && !(event.shiftKey && event.ctrlKey && event.altKey)) {
      return;
    }
    this.loginError = null;
    this.loginUsername = '';
    this.loginPassword = '';
    this.showLogin = true;
  }

  cancelLogin(): void {
    this.showLogin = false;
    this.loginError = null;
    this.loginPassword = '';
  }

  async submitLogin(): Promise<void> {
    if (this.loggingIn) return;
    this.loggingIn = true;
    this.loginError = null;
    try {
      const response = await this.http.postWithCsrf('/api/auth/login', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: this.loginUsername, password: this.loginPassword })
      });
      if (response.status === 401) {
        this.loginError = 'Invalid username or password.';
        return;
      }
      if (!response.ok) {
        this.loginError = `Sign in failed (HTTP ${response.status}).`;
        return;
      }
      this.isAdmin = true;
      this.showLogin = false;
      this.loginPassword = '';
      // The antiforgery token is bound to the user identity, so refresh it (via a GET)
      // now that we are authenticated; otherwise the first admin POST would be rejected.
      await this.checkAuth();
    } catch (e: any) {
      this.loginError = `Sign in failed: ${e.message}`;
    } finally {
      this.loggingIn = false;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.http.postWithCsrf('/api/auth/logout');
    } catch {
      // Ignore network errors on logout; clear local state regardless.
    }
    this.isAdmin = false;
    this.logExpanded = false;
    this.adminMenuOpen = false;
  }

  toggleAdminMenu(): void {
    this.adminMenuOpen = !this.adminMenuOpen;
  }

  closeAdminMenu(): void {
    this.adminMenuOpen = false;
  }

  toggleLog(): void {
    this.adminMenuOpen = false;
    this.logExpanded = !this.logExpanded;
    if (this.logExpanded) {
      // The element is created by this toggle, so scroll to the newest entry once
      // it has been rendered.
      this.diagnostics.scrollLogToBottom();
    }
  }

  clearLog(): void {
    this.diagnostics.clear();
  }
}
